import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Star, Clock, MapPin, Heart, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';
import NoImageTile from '@/components/NoImageTile';

export default function RestaurantHeader({ restaurant, onBack }) {
  const { isAuthenticated, token } = useAuthStore();
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const checkFavorite = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/favorites/check/${restaurant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error('Failed to check favorite:', error);
    }
  });

  useEffect(() => {
    if (isAuthenticated && restaurant?.id) {
      checkFavorite();
    }
  }, [isAuthenticated, restaurant?.id, checkFavorite]);

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to save favorites');
      return;
    }

    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${restaurant.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { restaurant_id: restaurant.id }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorite');
    } finally {
      setFavoriteLoading(false);
    }
  };

  return (
    <>
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80">
        {restaurant?.cover_image_url || restaurant?.image_url ? (
          <img
            src={restaurant?.cover_image_url || restaurant?.image_url}
            alt={restaurant?.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <NoImageTile name={restaurant?.name} className="w-full h-full" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="absolute top-4 left-4 bg-card/80 backdrop-blur-sm rounded-full"
          data-testid="back-button"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleFavorite}
          disabled={favoriteLoading}
          className="absolute top-4 right-4 bg-card/80 backdrop-blur-sm rounded-full"
          data-testid="favorite-button"
        >
          {favoriteLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Heart className={`h-5 w-5 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
          )}
        </Button>
      </div>

      {/* Restaurant Info Card */}
      <div className="max-w-7xl mx-auto px-4 -mt-16 relative z-10">
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{restaurant?.cuisine_type}</Badge>
                {!restaurant?.is_open && (
                  <Badge variant="destructive">Closed</Badge>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">{restaurant?.name}</h1>
              <p className="text-muted-foreground mb-4">{restaurant?.description}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                  <span className="font-medium">{restaurant?.rating?.toFixed(1)}</span>
                  <span className="text-muted-foreground">({restaurant?.total_reviews} reviews)</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {restaurant?.delivery_time_min}-{restaurant?.delivery_time_max} min
                </div>
                <div className="flex items-center gap-1 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {restaurant?.address}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Delivery fee</p>
              <p className="text-xl font-bold">${restaurant?.delivery_fee?.toFixed(2)}</p>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
